import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";
import {
  CaretRight,
  CaretLeft,
  SignOut,
  Bell,
  User as UserIcon,
  Target,
  Heartbeat,
  CheckCircle,
  Sparkle,
} from "@phosphor-icons/react";
import { subscribeToPush } from "../lib/push";

const TOTAL_STEPS = 5;

const SPORT_SUGGESTIONS = [
  "Fútbol", "Running", "Rugby", "Crossfit", "Powerlifting",
  "Tenis", "Ciclismo", "Natación", "Básquet", "Hockey", "Otro",
];

const GOAL_OPTIONS = [
  { key: "perder_peso", label: "Perder peso", emoji: "⚖️" },
  { key: "ganar_musculo", label: "Ganar músculo", emoji: "💪" },
  { key: "rendimiento", label: "Mejorar rendimiento", emoji: "🚀" },
  { key: "resistencia", label: "Resistencia aeróbica", emoji: "🏃" },
  { key: "fuerza", label: "Fuerza máxima", emoji: "🏋️" },
  { key: "rehab", label: "Rehabilitación", emoji: "🩹" },
];

export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [form, setForm] = useState({
    name: user?.name || "",
    birthdate: user?.profile?.birthdate || "",
    height_cm: user?.profile?.height_cm || "",
    weight_kg: user?.profile?.weight_kg || "",
    phone: user?.profile?.phone || "",
    sport: user?.profile?.sport || "",
    goals: [],
    notes: user?.profile?.notes || "",
    initial_rpe: "",
    initial_resting_hr: "",
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleGoal = (key) =>
    update({
      goals: form.goals.includes(key)
        ? form.goals.filter((g) => g !== key)
        : [...form.goals, key],
    });

  const validateStep = () => {
    if (step === 1) {
      if (!form.name.trim() || !form.birthdate || !form.height_cm || !form.weight_kg) {
        toast.error("Completá los datos básicos");
        return false;
      }
    }
    if (step === 2) {
      if (!form.sport.trim()) {
        toast.error("Elegí o escribí tu deporte");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    setLoading(true);
    try {
      const goalsNote = form.goals.length
        ? `Objetivos: ${form.goals.map((g) => GOAL_OPTIONS.find((o) => o.key === g)?.label).join(", ")}`
        : "";
      const combinedNotes = [form.notes.trim(), goalsNote].filter(Boolean).join(" · ");

      const profilePayload = {
        birthdate: form.birthdate,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        phone: form.phone,
        sport: form.sport,
        notes: combinedNotes,
      };
      if (form.initial_resting_hr) profilePayload.resting_hr = Number(form.initial_resting_hr);
      if (form.initial_rpe) profilePayload.baseline_rpe = Number(form.initial_rpe);
      if (form.goals.length) profilePayload.goals = form.goals;

      const { data } = await api.put(`/users/${user.user_id}`, {
        name: form.name,
        profile: profilePayload,
      });

      setUser({ ...user, ...data, onboarded: true });

      if (enableNotifications) {
        try {
          const res = await subscribeToPush();
          if (res.ok) toast.success("Notificaciones activadas");
        } catch { /* opcional */ }
      }

      toast.success("¡Bienvenido a Athlon Sports!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const progressPct = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-app py-6 sm:py-10 px-4 noise-bg">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Logo size={56} />
            <div>
              <div className="font-display font-black text-lg sm:text-xl tracking-tighter uppercase leading-tight">Athlon Sports</div>
              <div className="text-[10px] text-muted uppercase tracking-widest">Training Control</div>
            </div>
          </div>
          <button onClick={handleLogout} className="btn-ghost flex items-center gap-2 text-xs" data-testid="onboarding-logout">
            <SignOut size={14} weight="bold" /> Salir
          </button>
        </div>

        {/* Progress */}
        <div className="mb-6" data-testid="onboarding-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted">
              Paso {step + 1} de {TOTAL_STEPS}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-primary-app">{Math.round(progressPct)}%</span>
          </div>
          <div className="h-1 bg-[#262626]">
            <div
              className="h-full bg-[#FF3B30] transition-all duration-300"
              style={{ width: `${progressPct}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        <div className="card-base p-6 sm:p-8" data-testid={`onboarding-step-${step}`}>
          {/* STEP 0 — Welcome */}
          {step === 0 && (
            <div className="text-center py-4">
              <Sparkle size={48} weight="duotone" className="text-primary-app mx-auto mb-4" />
              <div className="text-xs uppercase tracking-widest text-primary-app mb-2">¡Bienvenido!</div>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter uppercase mb-4">
                Hola, {user?.name?.split(" ")[0] || "Atleta"}
              </h1>
              <p className="text-muted mb-6 leading-relaxed">
                Vamos a configurar tu perfil en 5 pasos rápidos. Te lleva menos
                de 2 minutos y nos sirve para personalizar tus rutinas, planes
                nutricionales y seguimiento.
              </p>
              <div className="grid grid-cols-2 gap-2 text-left mb-6">
                {[
                  { icon: UserIcon, label: "Datos personales" },
                  { icon: Target, label: "Tu deporte" },
                  { icon: Heartbeat, label: "Estado inicial" },
                  { icon: Bell, label: "Notificaciones" },
                ].map((it, i) => {
                  const Icon = it.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs bg-[#0A0A0A] border border-app p-2">
                      <Icon size={14} weight="duotone" className="text-primary-app" />
                      <span className="text-muted">{it.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 1 — Personal data */}
          {step === 1 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <UserIcon size={20} weight="duotone" className="text-primary-app" />
                <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter uppercase">Datos básicos</h2>
              </div>
              <p className="text-xs text-muted mb-5">Esto se usa para calcular IMC, ICC y calorías base.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nombre completo *</label>
                  <input className="input-base" value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="onb-name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nacimiento *</label>
                    <input type="date" className="input-base" value={form.birthdate} onChange={(e) => update({ birthdate: e.target.value })} required data-testid="onb-birthdate" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted block mb-2">Teléfono</label>
                    <input className="input-base" value={form.phone} onChange={(e) => update({ phone: e.target.value })} data-testid="onb-phone" placeholder="+54 11..." />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted block mb-2">Altura (cm) *</label>
                    <input type="number" className="input-base" value={form.height_cm} onChange={(e) => update({ height_cm: e.target.value })} required data-testid="onb-height" placeholder="178" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted block mb-2">Peso (kg) *</label>
                    <input type="number" step="0.1" className="input-base" value={form.weight_kg} onChange={(e) => update({ weight_kg: e.target.value })} required data-testid="onb-weight" placeholder="72" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Sport & goals */}
          {step === 2 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target size={20} weight="duotone" className="text-primary-app" />
                <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter uppercase">Tu deporte y objetivos</h2>
              </div>
              <p className="text-xs text-muted mb-5">¿Para qué entrenás? Esto guía a tu entrenador.</p>
              <div className="mb-5">
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Deporte principal *</label>
                <input className="input-base mb-2" value={form.sport} onChange={(e) => update({ sport: e.target.value })} placeholder="Escribí o elegí abajo" data-testid="onb-sport" />
                <div className="flex flex-wrap gap-2">
                  {SPORT_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => update({ sport: s })}
                      className={`text-[11px] px-3 py-1 border transition-colors ${form.sport === s ? "border-primary-app text-primary-app bg-primary-app/10" : "border-app text-muted hover:text-white"}`}
                      data-testid={`sport-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Objetivos (elegí 1-3)</label>
                <div className="grid grid-cols-2 gap-2">
                  {GOAL_OPTIONS.map((g) => {
                    const active = form.goals.includes(g.key);
                    return (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() => toggleGoal(g.key)}
                        className={`flex items-center gap-2 p-3 border text-left text-sm transition-colors ${active ? "border-primary-app bg-primary-app/10 text-white" : "border-app text-muted hover:text-white hover:border-white"}`}
                        data-testid={`goal-${g.key}`}
                      >
                        <span className="text-lg">{g.emoji}</span>
                        <span>{g.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Initial state (optional) */}
          {step === 3 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heartbeat size={20} weight="duotone" className="text-primary-app" />
                <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter uppercase">Estado inicial</h2>
                <span className="text-[10px] uppercase tracking-widest text-muted ml-1">opcional</span>
              </div>
              <p className="text-xs text-muted mb-5">
                Estos datos quedan como tu testeo inicial. Si no los sabés ahora, los podés cargar más tarde.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted block mb-2">Frecuencia cardíaca en reposo (bpm)</label>
                  <input type="number" className="input-base" value={form.initial_resting_hr} onChange={(e) => update({ initial_resting_hr: e.target.value })} placeholder="60" data-testid="onb-resting-hr" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted block mb-2">¿Cómo te sentís hoy? (RPE 1-10)</label>
                  <div className="grid grid-cols-10 gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => update({ initial_rpe: String(n) })}
                        className={`aspect-square font-display font-black text-sm border transition-all ${String(form.initial_rpe) === String(n) ? "border-primary-app bg-primary-app text-white" : "border-app text-muted hover:text-white hover:border-white"}`}
                        data-testid={`onb-rpe-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted block mb-2">Notas / lesiones (opcional)</label>
                  <textarea className="input-base" rows="3" value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Lesiones pasadas, restricciones, alergias..." data-testid="onb-notes" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 — Notifications + finish */}
          {step === 4 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Bell size={20} weight="duotone" className="text-primary-app" />
                <h2 className="font-display font-black text-xl sm:text-2xl tracking-tighter uppercase">¡Casi listo!</h2>
              </div>
              <p className="text-xs text-muted mb-5">
                Una sola cosa más y entrás a tu panel.
              </p>
              <label
                htmlFor="onb-enable-notifs"
                className="flex items-start gap-3 p-4 border border-app cursor-pointer hover:border-primary-app transition-colors mb-5"
                data-testid="onb-notifs-label"
              >
                <input
                  id="onb-enable-notifs"
                  type="checkbox"
                  checked={enableNotifications}
                  onChange={(e) => setEnableNotifications(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#FF3B30] cursor-pointer"
                  data-testid="onb-enable-notifs"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell size={16} weight="duotone" className="text-primary-app" />
                    <div className="font-display font-bold uppercase tracking-wider text-sm text-white">
                      Activar notificaciones push
                    </div>
                  </div>
                  <div className="text-xs text-muted leading-relaxed">
                    Recordatorios de entrenamiento, mensajes del entrenador, confirmaciones de pago y alertas wellness.
                  </div>
                </div>
              </label>

              <div className="bg-[#0A0A0A] border border-app p-4 mb-2">
                <div className="text-[10px] uppercase tracking-widest text-muted mb-3">Resumen</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted">Nombre:</span> <span className="text-white">{form.name || "—"}</span></div>
                  <div><span className="text-muted">Deporte:</span> <span className="text-white">{form.sport || "—"}</span></div>
                  <div><span className="text-muted">Altura:</span> <span className="text-white">{form.height_cm ? `${form.height_cm} cm` : "—"}</span></div>
                  <div><span className="text-muted">Peso:</span> <span className="text-white">{form.weight_kg ? `${form.weight_kg} kg` : "—"}</span></div>
                  {form.goals.length > 0 && (
                    <div className="col-span-2"><span className="text-muted">Objetivos:</span> <span className="text-white">{form.goals.map((g) => GOAL_OPTIONS.find((o) => o.key === g)?.label).join(", ")}</span></div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 gap-3">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="btn-secondary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="onb-back"
            >
              <CaretLeft size={14} weight="bold" /> Atrás
            </button>
            {step < TOTAL_STEPS - 1 ? (
              <button type="button" onClick={next} className="btn-primary flex items-center gap-2" data-testid="onb-next">
                {step === 3 ? "Saltar / continuar" : "Continuar"} <CaretRight size={14} weight="bold" />
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={loading}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="onb-submit"
              >
                {loading ? "Guardando..." : "Comenzar a entrenar"} <CheckCircle size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
